import React from "react";
import { Box, Divider, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import LinkIcon from "@icons/myicons/links.svg?react";
import ShieldLockIcon from "@icons/myicons/shield-lock.svg?react";
import UserAddIcon from "@icons/myicons/user-add.svg?react";
import { useBankImportReturnUrl, useMandatorLinks } from "@hooks";
import { Banner } from "@components/Banner";
import { Modal } from "@components/Modal/Modal";
import { useBankSelectModal } from "@components/BankSelect";
import { PremiumModalHeader } from "./PremiumModalHeader";
import { FeatureCards } from "./FeatureCards";
import { ProceedOption } from "./ProceedOption";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const PremiumFeaturesModal = ({ isOpen, onClose }: Props) => {
  const { t } = useTranslation();
  const { saveReturnUrl } = useBankImportReturnUrl();
  const { openBankSelect, bankSelectModal } = useBankSelectModal();
  const { premiumLandingUrl, openDepotUrl } = useMandatorLinks();

  // Boersennews has a dedicated premium landing page; other mandators fall back
  // to the generic "open a depot" page so both options stay available.
  const openDepotTarget = premiumLandingUrl ?? openDepotUrl;

  const handleConnectDepot = () => {
    saveReturnUrl();
    onClose();
    openBankSelect();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} width={700}>
        <PremiumModalHeader onClose={onClose} />

        <Typography variant="h4" color="textPrimary" mb={1}>
          {t("premium.featuresModal.title")}
        </Typography>
        <Typography variant="body3" color="textSecondary" mb={3}>
          {t("premium.featuresModal.subtitle")}
        </Typography>

        <FeatureCards />

        <Banner
          type="success"
          alignBannerItems="flex-start"
          icon={<ShieldLockIcon width="28px" height="28px" />}
          title={t("premium.featuresModal.freeBox.title")}
          text={t("premium.featuresModal.freeBox.text")}
        />

        <Divider sx={{ my: 6 }} />

        <Typography variant="body2" color="textSecondary" mb={4}>
          {t("premium.featuresModal.howToProceed")}
        </Typography>

        <Box display="flex" flexDirection="column" gap={3}>
          <ProceedOption
            icon={<LinkIcon width="24px" height="24px" />}
            title={t("premium.featuresModal.connectDepot.title")}
            subtitle={t("premium.featuresModal.connectDepot.subtitle")}
            onClick={handleConnectDepot}
          />
          {openDepotTarget && (
            <ProceedOption
              icon={<UserAddIcon width="24px" height="24px" />}
              title={t("premium.featuresModal.openDepot.title")}
              subtitle={t("premium.featuresModal.openDepot.subtitle")}
              onClick={onClose}
              href={openDepotTarget}
            />
          )}
        </Box>
      </Modal>
      {bankSelectModal}
    </>
  );
};
